import { useState, useEffect, useRef } from "react"
import { useGlobalContext } from "../../modules/data/GlobalContext"
import { motion } from "framer-motion"

export default function Notification({ onClose }) {
  const { messages, markMessageAsRead } = useGlobalContext()
  const [activeTab, setActiveTab] = useState("unread")
  const [expanded, setExpanded] = useState({})
  const [overflowing, setOverflowing] = useState({}) // track which notifications overflow

  function fixEncoding(str) {
    try {
      return decodeURIComponent(escape(str))
    } catch {
      return str
    }
  }

  const apiNotifications = (messages || []).map((msg) => ({
    id: msg.messageId,
    title: fixEncoding(msg.messageTitle),
    content: fixEncoding(msg.messageContent.replace(/<br\s*\/?>/gi, "<br/>")),
    from: msg.from,
    status: msg.status,
    type: msg.type || "user",
  }))

  const notifications = [
    ...apiNotifications,
    {
      id: 9991,
      title: "FGH is fully operational!",
      content: `
        <p>While <strong>GLife</strong> platforms are down, 
        <strong>FGH independent payment channels</strong> keep you playing anytime — no interruptions.</p>
        <p>Play directly on FGH and keep the fun going!</p>
      `,
      status: 1,
    },
  ]

  const filteredNotifications = notifications.filter((n) =>
    activeTab === "unread" ? n.status === 0 : n.status === 1
  )

  const item = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  }

  // Toggle expand/collapse
  const toggleExpand = (id) => {
    const el = contentRefs.current[id]
    if (!el) return

    const isExpanding = !expanded[id]
    const startHeight = el.clientHeight
    const endHeight = isExpanding ? el.scrollHeight : 60

    el.style.height = `${startHeight}px`
    requestAnimationFrame(() => {
      el.style.transition = "height 0.4s ease"
      el.style.height = `${endHeight}px`
    })

    const handleEnd = () => {
      el.style.height = ""
      el.style.transition = ""
      el.removeEventListener("transitionend", handleEnd)
    }
    el.addEventListener("transitionend", handleEnd)

    setExpanded((prev) => ({ ...prev, [id]: isExpanding }))
  }

  // Detect if content overflows
  const contentRefs = useRef({})

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      const newOverflowing = {}
      for (const id in contentRefs.current) {
        const el = contentRefs.current[id]
        if (el) {
          newOverflowing[id] = el.scrollHeight > el.clientHeight + 5
        }
      }
      setOverflowing(newOverflowing)
    })

    Object.values(contentRefs.current).forEach(
      (el) => el && observer.observe(el)
    )
    return () => observer.disconnect()
  }, [filteredNotifications])

  return (
    <div className="fgh-notification-section">
      <h1>Notifications</h1>

      <div className="fgh-tabs">
        {["read", "unread"].map((tab) => (
          <button
            key={tab}
            className={`fgh-tab-button ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {filteredNotifications.length > 0 ? (
        filteredNotifications.map((notification) => {
          const isExpanded = expanded[notification.id]
          const isOverflow = overflowing[notification.id]

          return (
            <motion.div
              key={notification.id}
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.7 }}
              variants={item}
              className={`fgh-notification-content ${
                notification.status === 1 ? "read" : ""
              } ${notification.type === "all" ? "global-message" : ""}`}
            >
              <h2>{notification.title}</h2>

              <div
                ref={(el) => (contentRefs.current[notification.id] = el)}
                className={`fgh-notification-body ${
                  isExpanded ? "expanded" : ""
                }`}
                dangerouslySetInnerHTML={{ __html: notification.content }}
              />

              {(isOverflow || isExpanded) && (
                <button
                  className="show-more-btn"
                  onClick={() => toggleExpand(notification.id)}
                >
                  {isExpanded ? "Show less" : "Show more"}
                </button>
              )}

              <div className="fgh-notification-links">
                {notification.type !== "all" && (
                  <button
                    onClick={() => markMessageAsRead(notification.id)}
                    disabled={notification.status === 1}
                  >
                    {notification.status === 1
                      ? "Already Read"
                      : "Mark as read"}
                  </button>
                )}
              </div>
            </motion.div>
          )
        })
      ) : (
        <div className="fgh-notification-no-data">
          <img src="/images/character/FGH-OPEN_EMAIL.webp" alt="" />
          <p className="fgh-no-notifs">
            {activeTab === "unread"
              ? "You’re all caught up! 🎉"
              : "No read notifications yet"}
          </p>
        </div>
      )}

      <div className="fgh-notification-btn-container">
        <button onClick={onClose}>Close</button>
      </div>
    </div>
  )
}
